import CrossIcon from "@atlaskit/icon/core/cross";
import type {Dispatch, ReactNode, SetStateAction} from "react";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {BottomDrawer} from "~/components/BottomDrawer";
import {EventContainerEditPanel} from "~/components/eventContainerComponents/eventContainerEditPanel";
import type {EventContainerAddEventItem} from "~/components/eventContainerAddEventButton";
import {
    JourneyProfileAsideCreateSurface,
    JourneyProfileCreateOptionRows,
} from "~/components/entityProfile/journeyProfileAsideCreateSurface";
import {
    JourneyProfileAsideMoreSurface,
    JourneyProfileFooterMoreOptionRows,
} from "~/components/entityProfile/journeyProfileFooterMoreSurface";
import type {JourneyProfileFooterMoreActionId} from "~/components/entityProfile/types";
import {AsidePanelPortal} from "~/components/ottopilotDataViewer/asidePanelPortal";
import {SidebarModal} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {StandardModalHeaderDivider} from "~/components/standardModalHeader";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {
    ArchiveEventContainerModal,
    DeleteEventContainerModal,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/event-containers/$eventContainerId/index/-archiveModals";
import type {EventContainerWithEvents} from "~/utilities/apis/api";

/** Page keeps the workspace header visible (z-[100]); the aside sits in a Mantine modal (200). */
const MOBILE_PAGE_SHEET_Z_INDEX = 100;
const MOBILE_ASIDE_SHEET_Z_INDEX = 300;

/** Full-panel edit UI for the Lifecycle profile aside (replaces profile; no stack). */
export function JourneyProfileAsideEditSurface({
    journey,
    onClose,
}: {
    journey: EventContainerWithEvents;
    onClose: () => void;
}): ReactNode {
    const eventTemplates = useEventTemplates();
    const eventContainerTemplate = useRequiredEventContainerTemplate(journey.templateId);
    const notesEventContainerSourceLabel =
        eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase;

    return (
        <div className="grid h-full min-h-0 min-w-0 w-full grid-rows-[auto_auto_minmax(0,1fr)] overflow-hidden bg-ds-elevation-surface-current">
            <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-200 px-ds-space-250 py-ds-space-150">
                <AnankeHeading size="small">Edit</AnankeHeading>
                <AnankeIconButton
                    type="button"
                    appearance="subtle"
                    icon={CrossIcon}
                    label="Close"
                    onClick={onClose}
                />
            </div>
            <StandardModalHeaderDivider />
            <div className="relative min-h-0 min-w-0 overflow-hidden">
                <div className="absolute inset-0 min-h-0 min-w-0 overflow-y-auto">
                    <EventContainerEditPanel
                        row={journey}
                        isExpanded={false}
                        eventContainerTemplate={eventContainerTemplate}
                        eventTemplates={eventTemplates}
                        notesEventContainerSourceLabel={notesEventContainerSourceLabel}
                        onSaveSuccess={onClose}
                    />
                </div>
            </div>
        </div>
    );
}

/**
 * Edit / create / more surfaces plus archive-delete modals for the journey profile.
 *
 * Mobile uses bottom sheets for create and more; edit uses a full-height modal.
 * Desktop page portals create/more into the aside column; aside presentation uses
 * exclusive surfaces for edit/create via the controller.
 */
export function JourneyProfileEngagementChrome({
    journey,
    isEditPanelOpen,
    onCloseEditPanel,
    isCreatePanelOpen,
    onCloseCreatePanel,
    createEventItems,
    isMorePanelOpen,
    onCloseMorePanel,
    moreActionItems,
    onMoreActionSelect,
    scheduleCallModal,
    isArchiveModalOpened,
    onArchiveModalOpenChange,
    isDeleteModalOpened,
    onDeleteModalOpenChange,
    archiveDeleteLoading,
    onArchiveDeleteLoadingChange,
    showArchiveAction,
    presentation = "page",
}: {
    journey: EventContainerWithEvents;
    isEditPanelOpen: boolean;
    onCloseEditPanel: () => void;
    isCreatePanelOpen: boolean;
    onCloseCreatePanel: () => void;
    createEventItems: Array<EventContainerAddEventItem>;
    isMorePanelOpen: boolean;
    onCloseMorePanel: () => void;
    moreActionItems: Array<{id: JourneyProfileFooterMoreActionId; label: string}>;
    onMoreActionSelect: (actionId: JourneyProfileFooterMoreActionId) => void;
    scheduleCallModal: ReactNode;
    isArchiveModalOpened: boolean;
    onArchiveModalOpenChange: Dispatch<SetStateAction<boolean>>;
    isDeleteModalOpened: boolean;
    onDeleteModalOpenChange: Dispatch<SetStateAction<boolean>>;
    archiveDeleteLoading: boolean;
    onArchiveDeleteLoadingChange: Dispatch<SetStateAction<boolean>>;
    showArchiveAction: boolean;
    presentation?: "page" | "aside";
}) {
    const isMobile = useCheckMobile();
    const eventTemplates = useEventTemplates();
    const eventContainerTemplate = useRequiredEventContainerTemplate(journey.templateId);
    const notesEventContainerSourceLabel =
        eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase;

    const editPanel = (
        <EventContainerEditPanel
            row={journey}
            isExpanded={false}
            eventContainerTemplate={eventContainerTemplate}
            eventTemplates={eventTemplates}
            notesEventContainerSourceLabel={notesEventContainerSourceLabel}
            onSaveSuccess={onCloseEditPanel}
        />
    );

    const archiveDeleteModals = (
        <>
            {showArchiveAction ? (
                <ArchiveEventContainerModal
                    isOpen={isArchiveModalOpened}
                    setIsOpen={onArchiveModalOpenChange}
                    loading={archiveDeleteLoading}
                    setLoading={onArchiveDeleteLoadingChange}
                    eventContainer={journey}
                />
            ) : (
                <DeleteEventContainerModal
                    isOpen={isDeleteModalOpened}
                    setIsOpen={onDeleteModalOpenChange}
                    loading={archiveDeleteLoading}
                    setLoading={onArchiveDeleteLoadingChange}
                    eventContainer={journey}
                />
            )}
        </>
    );

    const sheetZIndex = presentation === "aside" ? MOBILE_ASIDE_SHEET_Z_INDEX : MOBILE_PAGE_SHEET_Z_INDEX;

    const handleMoreActionSelect = (actionId: JourneyProfileFooterMoreActionId) => {
        onCloseMorePanel();
        onMoreActionSelect(actionId);
    };

    if (isMobile === true) {
        return (
            <>
                {isEditPanelOpen === true ? (
                    <SidebarModal
                        headerLeft="Edit"
                        useStandardCloseButton={true}
                        body={editPanel}
                        onClose={onCloseEditPanel}
                    />
                ) : null}

                <BottomDrawer
                    open={isCreatePanelOpen === true && createEventItems.length > 0}
                    setOpen={(open) => {
                        if (open === false) {
                            onCloseCreatePanel();
                        }
                    }}
                    title="Create"
                    zIndex={sheetZIndex}
                    initialOpenHeight={480}
                >
                    <JourneyProfileCreateOptionRows
                        items={createEventItems}
                        onNavigate={onCloseCreatePanel}
                    />
                </BottomDrawer>

                <BottomDrawer
                    open={isMorePanelOpen === true && moreActionItems.length > 0}
                    setOpen={(open) => {
                        if (open === false) {
                            onCloseMorePanel();
                        }
                    }}
                    title="More actions"
                    zIndex={sheetZIndex}
                    fitContentOnOpen={true}
                    compactLayout={true}
                >
                    <JourneyProfileFooterMoreOptionRows
                        items={moreActionItems}
                        onSelect={handleMoreActionSelect}
                    />
                </BottomDrawer>

                {scheduleCallModal}
                {archiveDeleteModals}
            </>
        );
    }

    return (
        <>
            {presentation === "page" && isEditPanelOpen === true ? (
                <AsidePanelPortal isFullscreen={false}>
                    <JourneyProfileAsideEditSurface journey={journey} onClose={onCloseEditPanel} />
                </AsidePanelPortal>
            ) : null}

            {presentation === "page" && isCreatePanelOpen === true && createEventItems.length > 0 ? (
                <AsidePanelPortal isFullscreen={false}>
                    <JourneyProfileAsideCreateSurface
                        items={createEventItems}
                        onClose={onCloseCreatePanel}
                    />
                </AsidePanelPortal>
            ) : null}

            {isMorePanelOpen === true && moreActionItems.length > 0 ? (
                <AsidePanelPortal isFullscreen={false}>
                    <JourneyProfileAsideMoreSurface
                        items={moreActionItems}
                        onClose={onCloseMorePanel}
                        onSelect={handleMoreActionSelect}
                    />
                </AsidePanelPortal>
            ) : null}

            {scheduleCallModal}
            {archiveDeleteModals}
        </>
    );
}
